import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import Ico from './ui/Ico';

const fmtFull = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(n || 0);

// Valores muito grandes (>= 1 bilhão) são abreviados para não estourar o card
const fmt = (n) =>
  Math.abs(n || 0) >= 1e9
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 2 }).format(n)
    : fmtFull(n);

const fmtNum = (n) => (n || 0).toLocaleString('pt-BR');

function StatCard({ title, value, fullValue, description, icon, gradient, bgGradient }) {
  return (
    <Card className={`bg-gradient-to-br ${bgGradient} border-0 shadow-lg`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-600">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-2 break-words" title={fullValue}>{value}</p>
            <p className="text-xs text-slate-500 mt-1">{description}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-lg shrink-0`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard({ api, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, d, f] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/dashboard/advanced-stats'),
        api.get('/deals?page=1&limit=5'),
        api.get('/funnels/list'),
      ]);
      if (s.status === 'fulfilled') setStats(s.value.data);
      if (a.status === 'fulfilled') setAdvancedStats(a.value.data);
      if (d.status === 'fulfilled') setDeals(d.value.data?.items || d.value.data?.deals || []);
      if (f.status === 'fulfilled') setFunnels(f.value.data || []);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const totalDealsValue = stats?.open_deals_value ?? 0;
  const openDeals = stats?.open_deals_count ?? 0;

  const nav = (page) => onNavigate && onNavigate(page);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-8 bg-slate-200 rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: 'Total de Leads',
      value: fmtNum(advancedStats?.basic_stats?.total_leads || stats?.total_leads),
      description: 'Todos os leads cadastrados',
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: 'Leads Este Mês',
      value: fmtNum(advancedStats?.basic_stats?.leads_this_month || stats?.new_leads),
      description: 'Novos leads no período',
      gradient: 'from-cyan-500 to-teal-600',
      bgGradient: 'from-cyan-50 to-teal-50',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
        </svg>
      ),
    },
    {
      title: 'Negócios em Aberto',
      value: fmtNum(openDeals),
      description: 'Negócios ativos no pipeline',
      gradient: 'from-violet-500 to-purple-600',
      bgGradient: 'from-violet-50 to-purple-50',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Valor em Pipeline',
      value: fmt(totalDealsValue),
      fullValue: fmtFull(totalDealsValue),
      description: 'Soma dos negócios abertos',
      gradient: 'from-emerald-500 to-green-600',
      bgGradient: 'from-emerald-50 to-green-50',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
    },
  ];

  const conversionRate = advancedStats?.basic_stats?.conversion_rate || 0;
  const forecast = advancedStats?.sales_forecast || {};
  const alerts = advancedStats?.smart_alerts || [];
  const highValueLeads = advancedStats?.ai_insights?.high_value_leads || 0;
  const coldLeads = advancedStats?.ai_insights?.cold_leads || 0;

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Visão geral da Leads Journey</p>
        </div>
        <Button onClick={loadAll} variant="outline" className="bg-white/60 backdrop-blur-sm border-white/30 hover:bg-white/80">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      {/* IA Forecast + Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Previsão de Vendas IA */}
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl text-slate-800 flex items-center gap-2">
              <Ico e="🤖" />Previsão de Vendas
              <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                {Math.round((forecast.confidence || 0) * 100)}% confiança
              </Badge>
            </CardTitle>
            <CardDescription>Análise preditiva com IA</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">Receita Prevista</span>
              <span className="text-2xl font-bold text-emerald-600">{fmt(forecast.predicted_revenue)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-blue-600">{highValueLeads}</div>
                <div className="text-xs text-slate-600 mt-0.5">Leads Alta Qualidade</div>
                <div className="text-xs text-blue-500">Score &gt; 80</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-red-500">{coldLeads}</div>
                <div className="text-xs text-slate-600 mt-0.5">Leads Frios</div>
                <div className="text-xs text-red-500">Score &lt; 30</div>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Taxa de Conversão Global</p>
              <div className="flex items-center gap-3">
                <Progress value={conversionRate} className="h-2 flex-1" />
                <span className="text-sm font-bold text-slate-700">{conversionRate}%</span>
              </div>
            </div>

            {(forecast.factors || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {(forecast.factors || []).map((f, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">{f}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alertas Inteligentes */}
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl text-slate-800"><Ico e="🚨" />Alertas Inteligentes</CardTitle>
            <CardDescription>Ações prioritárias identificadas pelo sistema</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-72 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center text-slate-500 py-6"><Ico e="🎉" />Nenhum alerta no momento!</div>
            ) : alerts.map((alert, i) => (
              <div key={i} className={`p-3 rounded-lg border-l-4 ${
                alert.priority === 'urgent' ? 'bg-red-50 border-red-500' :
                alert.priority === 'high'   ? 'bg-orange-50 border-orange-500' :
                alert.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                'bg-blue-50 border-blue-500'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-medium text-slate-800 text-sm">{alert.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{alert.message}</div>
                  </div>
                  <Badge variant="outline" className={`text-xs shrink-0 ${
                    alert.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    alert.priority === 'high'   ? 'bg-orange-100 text-orange-700' :
                    alert.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-100 text-blue-700'}`}>
                    {alert.priority}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Negócios Recentes + Ações Rápidas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Negócios Recentes */}
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-xl text-slate-800"><Ico e="💼" />Negócios Recentes</CardTitle>
              <CardDescription>Últimos negócios em aberto</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => nav('deals')}>
              Ver todos
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {deals.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                <div className="text-3xl mb-2"><Ico e="🤝" bare /></div>
                <p className="text-sm">Nenhum negócio aberto</p>
                <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav('deals')}>
                  Criar Negócio
                </Button>
              </div>
            ) : deals.map((deal) => (
              <div key={deal.id} className="flex items-center justify-between p-3 rounded-lg bg-white/60 hover:bg-white/80 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 text-sm truncate">{deal.title}</p>
                  <p className="text-xs text-slate-500 truncate">{deal.person_name} · {deal.stage}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="font-bold text-emerald-600 text-sm">{fmt(deal.value)}</p>
                  <p className="text-xs text-slate-400">{deal.probability}%</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Ações Rápidas */}
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl text-slate-800"><Ico e="⚡" />Ações Rápidas</CardTitle>
            <CardDescription>Acesse rapidamente os módulos principais</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            {[
              {
                label: 'Novo Lead', sub: 'Leads Journey', icon: <Ico e="👤" bare />,
                color: 'from-blue-500 to-indigo-600', page: 'leads',
              },
              {
                label: 'Negócios', sub: 'Pipeline de vendas', icon: <Ico e="💼" bare />,
                color: 'from-violet-500 to-purple-600', page: 'deals',
              },
              {
                label: 'Pipeline', sub: 'Kanban de funis', icon: <Ico e="📊" bare />,
                color: 'from-cyan-500 to-teal-600', page: 'pipeline',
              },
              {
                label: 'NexoProject', sub: 'Projetos entregues', icon: <Ico e="🚀" bare />,
                color: 'from-orange-500 to-red-500', page: 'projects',
              },
              {
                label: 'Prospecções', sub: 'Listas & campanhas', icon: <Ico e="🔍" bare />,
                color: 'from-emerald-500 to-green-600', page: 'lead-extraction',
              },
              {
                label: 'Criação IA', sub: 'Conteúdo com IA', icon: <Ico e="✨" bare />,
                color: 'from-pink-500 to-rose-600', page: 'creation',
              },
            ].map((item) => (
              <button
                key={item.page}
                onClick={() => nav(item.page)}
                className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br ${item.color} text-white shadow-md hover:shadow-lg hover:scale-[1.02] transition-all text-left`}
              >
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <div className="font-semibold text-sm leading-tight">{item.label}</div>
                  <div className="text-xs opacity-80 mt-0.5">{item.sub}</div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Funis Ativos */}
      {funnels.length > 0 && (
        <Card className="glass border-white/20 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-xl text-slate-800"><Ico e="🌀" />Funis Ativos</CardTitle>
              <CardDescription>Estágios configurados por funil de vendas</CardDescription>
            </div>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => nav('funnels')}>
              Configurar
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {funnels.map((funnel) => (
                <div key={funnel.id} className="rounded-xl border border-slate-100 bg-white/60 p-4">
                  <p className="font-semibold text-slate-800 text-sm mb-3 truncate">{funnel.name}</p>
                  <div className="space-y-1.5">
                    {(funnel.stages || []).slice(0, 5).map((stage, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: `hsl(${(i * 47) % 360}, 70%, 55%)` }}
                        />
                        <span className="text-xs text-slate-600 truncate">{stage}</span>
                      </div>
                    ))}
                    {(funnel.stages || []).length > 5 && (
                      <p className="text-xs text-slate-400 pl-4">+{funnel.stages.length - 5} estágios</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
